import './main.css';
import * as env from './env.json';
import Map from 'ol/Map';
import View from 'ol/View';
import {Tile as TileLayer} from 'ol/layer';
import {TileDebug, OSM} from 'ol/source';
import {GeoJSON} from 'ol/format';
import {createStringXY} from 'ol/coordinate';
import {fromLonLat, toLonLat} from 'ol/proj';
import Overlay from 'ol/Overlay';
import {Icon, Fill, Style, Text} from 'ol/style';
import {createXYZ} from 'ol/tilegrid';
import {Control, Attribution, MousePosition, defaults as defaultControls} from 'ol/control';
import sync from 'ol-hashed';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import {circular} from 'ol/geom/Polygon';
import {getCenter} from 'ol/extent';

// --- Config ---

function apiUrl(path) {
  const base = env.nswfuelwatch.basePath || '';
  return `${base}${path}`;
}

// --- Debounced loader with proximity dedup ---

const LOAD_DEBOUNCE_MS = 400;
const MIN_MOVE_KM = 1.0;

let lastLoadCenter = null;
let loadTimer = null;
let activeAbort = null;

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function loadFuelData() {
  const center = map.getView().getCenter();
  const coordLonLat = toLonLat(center);
  const lon = coordLonLat[0];
  const lat = coordLonLat[1];

  if (lastLoadCenter) {
    const dist = haversineKm(lastLoadCenter[1], lastLoadCenter[0], lat, lon);
    if (dist < MIN_MOVE_KM) return;
  }

  if (activeAbort) activeAbort.abort();
  const controller = new AbortController();
  activeAbort = controller;

  const fuelType = document.querySelector('input[name="fueltype"]:checked').value;
  const url = apiUrl(`/fuelnearme.geojson?lat=${lat}&lon=${lon}&fueltype=${fuelType}`);

  fetch(url, { signal: controller.signal })
    .then(response => {
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => {
      const features = new GeoJSON().readFeatures(data, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      sourceFuelWatch.clear(true);
      sourceFuelWatch.addFeatures(features);
      lastLoadCenter = [lon, lat];
    })
    .catch(err => {
      if (err.name !== 'AbortError') {
        console.warn('Fuel data load failed:', err.message);
      }
    })
    .finally(() => {
      if (activeAbort === controller) activeAbort = null;
    });
}

function scheduleLoad() {
  if (loadTimer) clearTimeout(loadTimer);
  loadTimer = setTimeout(loadFuelData, LOAD_DEBOUNCE_MS);
}

function forceReload() {
  lastLoadCenter = null;
  scheduleLoad();
}

// --- Map data sources ---

const sydney = fromLonLat([151.2061999882396, -33.8732161200895]);

const sourceFuelWatch = new VectorSource();

// --- Styles (cached per price value) ---

const cachedIcon = new Icon({
  src: '/fuelstation.svg',
  scale: 0.02,
});

const styleCache = new Map();

function pointStyleFunction(feature) {
  const price = feature.get('price');
  const key = String(price);
  let style = styleCache.get(key);
  if (!style) {
    style = new Style({
      image: cachedIcon,
      text: new Text({
        textAlign: 'left',
        offsetX: 14,
        text: key,
        font: 'bold 16px Calibri,sans-serif',
        fill: new Fill({ color: '#0000CD' }),
      }),
      zIndex: Infinity,
    });
    styleCache.set(key, style);
  }
  return style;
}

// --- Layers ---

const fuelWatchLayer = new VectorLayer({
    source: sourceFuelWatch,
    style: pointStyleFunction,
    declutter: true,
    minZoom: 12,
});

const sourceLocation = new VectorSource();
const locationLayer = new VectorLayer({
  source: sourceLocation,
});

const debugLayer = new TileLayer({
    source: new TileDebug({
        projection: 'EPSG:3857',
        tileGrid: createXYZ({ maxZoom: 21 }),
    }),
    visible: false,
});

const basemapLayer = new TileLayer({
    source: new OSM(),
});

// --- Map setup ---

const view = new View({
  center: sydney,
  zoom: 14,
});

const attribution = new Attribution({
  collapsible: false,
});

const map = new Map({
  target: 'map',
  layers: [
    basemapLayer,
    fuelWatchLayer,
    debugLayer,
    locationLayer,
  ],
  controls: defaultControls({attribution: false}).extend([attribution]),
  view: view,
});

function onClick(id, callback) {
  document.getElementById(id).addEventListener('click', callback);
}

onClick('fly-to-sydney', function() {
  view.setCenter(sydney);
});

// --- Load on map move (debounced) ---

map.on('moveend', scheduleLoad);
loadFuelData();

// --- Control panel toggle ---

const fuelTypeLabels = {
  'E10': 'Ethanol 94 (E10)',
  'E10-U91': 'E10 / Unleaded 91',
  'U91': 'Unleaded 91',
  'P95': 'Premium 95',
  'P98': 'Premium 98',
  'PDL': 'Diesel',
};

const ctrlPanel = document.getElementById('ctrl-panel');
const ctrlToggle = document.getElementById('ctrl-panel-toggle');

ctrlToggle.addEventListener('click', () => {
  ctrlPanel.classList.toggle('collapsed');
});

function updateToggleLabel() {
  const selected = document.querySelector('input[name="fueltype"]:checked').value;
  ctrlToggle.textContent = 'Fuel: ' + (fuelTypeLabels[selected] || selected);
}

// --- Popup with auto-close timer ---

const POPUP_TIMEOUT_MS = 4000;
let popupTimer = null;

function closePopup() {
  overlay.setPosition(undefined);
}

function resetPopupTimer() {
  if (popupTimer) clearTimeout(popupTimer);
  popupTimer = setTimeout(closePopup, POPUP_TIMEOUT_MS);
}

function buildPopup(container, properties) {
  container.innerHTML = '';
  const pre = document.createElement('pre');
  pre.appendChild(document.createTextNode('Fuel: ' + String(properties["fueltype"] || '')));
  pre.appendChild(document.createTextNode('\n'));
  pre.appendChild(document.createTextNode('Price: ' + String(properties["price"])));
  pre.appendChild(document.createTextNode('\n'));
  const addr = String(properties["address"] || "");
  const link = document.createElement('a');
  link.href = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(addr);
  link.textContent = addr;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  pre.appendChild(link);
  container.appendChild(pre);
}

map.on('pointermove', function(evt) {
  const feature_onHover = map.forEachFeatureAtPixel(evt.pixel, function(feature) {
    return feature;
  }, {hitTolerance: 10});

  if (feature_onHover == null) return;

  const properties = feature_onHover.getProperties();
  if (properties["fueltype"] == null) return;

  buildPopup(document.getElementById('popup-content'), properties);
  overlay.setPosition(evt.coordinate);
  resetPopupTimer();
});

const mousePositionControl = new MousePosition({
  coordinateFormat: createStringXY(4),
  projection: 'EPSG:4326',
});

map.addControl(mousePositionControl);

const popupEl = document.getElementById('popup');
const closer = document.getElementById('popup-closer');

const overlay = new Overlay({
  element: popupEl,
  autoPan: true,
  autoPanAnimation: { duration: 250 },
});
map.addOverlay(overlay);

closer.onclick = function() {
  closePopup();
  closer.blur();
  return false;
};

// --- Fuel type selector ---

document.querySelectorAll("input[name='fueltype']").forEach((input) => {
    input.addEventListener('change', () => {
      updateToggleLabel();
      ctrlPanel.classList.add('collapsed');
      forceReload();
    });
});

document.getElementById("checkbox-debug").addEventListener('change', function() {
  debugLayer.setVisible(this.checked);
});

// --- Geolocation ---

let initialLocate = true;

navigator.geolocation.watchPosition(
  function (pos) {
    const coords = [pos.coords.longitude, pos.coords.latitude];
    const accuracy = circular(coords, pos.coords.accuracy);
    sourceLocation.clear(true);
    sourceLocation.addFeatures([
      new Feature(accuracy.transform('EPSG:4326', map.getView().getProjection())),
      new Feature(new Point(fromLonLat(coords))),
    ]);
    if (initialLocate) {
      initialLocate = false;
      map.getView().animate({ center: fromLonLat(coords), zoom: 14, duration: 500 });
    }
  },
  function (error) {
    console.warn(`Geolocation error: ${error.message}`);
  },
  { enableHighAccuracy: true },
);

const locate = document.createElement('div');
locate.className = 'ol-control ol-unselectable locate';
locate.innerHTML = '<button title="Locate me">&#x25CE;</button>';
locate.addEventListener('click', function () {
  if (!sourceLocation.isEmpty()) {
    map.getView().fit(sourceLocation.getExtent(), {
      maxZoom: 15,
      duration: 500,
    });
  }
});
map.addControl(new Control({ element: locate }));

// Group zoom + locate in a flex container below the ctrl-panel
setTimeout(() => {
  const mapEl = map.getTargetElement();
  const container = document.createElement('div');
  container.className = 'left-controls';
  const zoom = mapEl.querySelector('.ol-zoom');
  const loc = mapEl.querySelector('.locate');
  if (zoom) container.appendChild(zoom);
  if (loc) container.appendChild(loc);
  const viewport = mapEl.querySelector('.ol-overlaycontainer-stopevent');
  if (viewport) viewport.appendChild(container);
  else mapEl.appendChild(container);
}, 0);

sync(map);
